import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { getAllUsersConsents, revokeConsent } from '@/api/consent.api'
import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'
import { UserConsentResponse } from '@/api/consent.api'
import { resetStore } from '@/store/actions/reset'

import { createRodoPolicyDocumentData } from './rodoPoliciesDocuments-slice'
import { createDataProcessingConsentDocument } from './dataProcessingConsents-slice'

export type RodoConsentRow = {
  id: string
  userId: string
  fullName: string
  email: string
  firm: string
  approvedAt: string
  rodoPolicyDocument: {
    documentationFile: { content: string; name: string; type: string }
  }
  dataProcessingConsent: {
    documentationFile: { content: string; name: string; type: string }
  }
  revoked?: boolean
  revokedAt?: string | null
}

export type UserConsentsState = {
  items: RodoConsentRow[]
  status: Status
  revokeStatus: Status
  error: string | null
}

const initialState: UserConsentsState = {
  items: [],
  status: 'idle',
  revokeStatus: 'idle',
  error: null,
}

const mapUserConsentToRow = ({
  id,
  acceptedAt,
  rodoPolicyDocument,
  dataProcessingConsent,
  revokedAt,
  user,
}: UserConsentResponse): RodoConsentRow => {
  const { id: userId, email, firstName, lastName, firms } = user

  return {
    id,
    userId,
    fullName: `${firstName} ${lastName}`,
    email,
    firm: firms.map(({ name }) => name).join(', '),
    approvedAt: new Date(acceptedAt).toISOString().slice(0, 10),
    rodoPolicyDocument,
    dataProcessingConsent,
    revoked: !!revokedAt,
    revokedAt: revokedAt ? new Date(revokedAt).toISOString().slice(0, 10) : null,
  }
}

export const getAllUsersConsentsData = createAsyncThunk<RodoConsentRow[], void>(
  'userConsents/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllUsersConsents()

      return res.map(mapUserConsentToRow)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const revokeUserConsent = createAsyncThunk<void, string>(
  'userConsents/revoke',
  async (consentId: string, { rejectWithValue }) => {
    try {
      await revokeConsent(consentId)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const userConsentSlice = createSlice({
  name: 'userConsents',
  initialState,
  reducers: {
    resetUserConsentsStatus: (state) => {
      state.status = 'idle'
      state.revokeStatus = 'idle'
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllUsersConsentsData.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(revokeUserConsent.pending, (state) => {
        state.revokeStatus = 'loading'
      })
      .addCase(getAllUsersConsentsData.rejected, (state) => {
        state.status = 'failed'
      })
      .addCase(revokeUserConsent.rejected, (state) => {
        state.revokeStatus = 'failed'
      })
      .addCase(getAllUsersConsentsData.fulfilled, (state, action) => {
        state.items = action.payload
        state.status = 'succeeded'
      })
      .addCase(revokeUserConsent.fulfilled, (state, action) => {
        const consentId = action.meta.arg

        const now = new Date().toISOString().slice(0, 10)

        const idx = state.items.findIndex((row) => row.id === consentId)
        if (idx !== -1) {
          state.items[idx] = {
            ...state.items[idx],
            revoked: true,
            revokedAt: now,
          }
        }
        state.revokeStatus = 'succeeded'
      })
      .addCase(createRodoPolicyDocumentData.fulfilled, (state) => {
        const now = new Date().toISOString().slice(0, 10)
        state.items = state.items.map((row) => ({
          ...row,
          revoked: true,
          revokedAt: now,
        }))
      })
      .addCase(createDataProcessingConsentDocument.fulfilled, (state) => {
        const now = new Date().toISOString().slice(0, 10)
        state.items = state.items.map((row) => ({
          ...row,
          revoked: true,
          revokedAt: now,
        }))
      })
      .addCase(resetStore, () => initialState)
  },
})

export const { resetUserConsentsStatus } = userConsentSlice.actions
export default userConsentSlice.reducer
